import {
  CircularProgress,
  DialogTitle,
  Divider,
  Stack,
  Box,
  Typography,
} from "@mui/material";
import {
  CloseIconWrapper,
  CreateBoardWrapper,
  FontGrayNormal,
  ModalIconWrapper,
  ModalTitle,
  StyledContainedButton,
  StyledDialog,
} from "./style";
import { CloseIcon, CreateFolderIcon } from "../../assets/Icons";
import { useDispatch, useSelector } from "react-redux";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { ErrorMessage } from "@hookform/error-message";
import {
  openAssignSeatModalSelector,
  seatSeactionLoaderSelector,
} from "../../store/metadata/selector";
import { addTeamMemberValidationSchema } from "../../utils/formValidationSchema";
import { useTheme } from "styled-components";
import { MetaDataSagaActions } from "../../store/metadata/sagas";
import { MetaDataReducerAction } from "../../store/metadata/slice";
import { StyledOutlinedInput } from "../commonComponents/formFields/style";
import { CustomButton } from "../../globalStyle";

type AssignSeatFormValues = {
  email: string;
};

const AssignSeatModal = () => {
  const dispatch = useDispatch();
  const openModal = useSelector(openAssignSeatModalSelector);
  const seatSeactionLoader = useSelector(seatSeactionLoaderSelector);
  const theme = useTheme();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<AssignSeatFormValues>({
    resolver: yupResolver(addTeamMemberValidationSchema),
  });

  const handleClose = () => {
    dispatch(MetaDataReducerAction.setOpenAssignSeatModal(""));
    reset();
  };

  const onSubmit = (data: AssignSeatFormValues) => {
    dispatch(
      MetaDataSagaActions.assignSeatToMember({
        email: data.email.toLowerCase(),
        seatId: openModal,
        callback: handleClose,
      })
    );
  };

  return (
    <StyledDialog
      open={!!openModal?.length}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle sx={{ padding: "1.875rem 1.875rem 1.25rem" }}>
        <Stack direction="row" gap="0.75rem" alignItems="flex-start">
          <ModalIconWrapper>
            <CreateFolderIcon fill="white" />
          </ModalIconWrapper>
          <Stack>
            <ModalTitle>Assign Seat</ModalTitle>
            <FontGrayNormal>
              Assign seat via sending invitation on email
            </FontGrayNormal>
          </Stack>
        </Stack>
        <CloseIconWrapper onClick={handleClose}>
          <CloseIcon />
        </CloseIconWrapper>
      </DialogTitle>

      <Divider sx={{ borderColor: "#E3E6EA" }} />

      <CreateBoardWrapper>
        <Stack spacing={2}>
          <Box
            sx={{
              width: "100%",
              display: "flex",
              gap: "1rem",
              alignItems: "center",
            }}
          >
            <StyledOutlinedInput
              id="email"
              type="email"
              placeholder="Member Email"
              {...register("email")}
              sx={{
                flex: 1,
                borderRadius: "1.688rem",
                color: `${theme.colors.gray1} !important`,
                border: `1px solid ${theme.colors.border1}`,
                height: "3.375rem",
                fontSize: "1rem",
              }}
            />
            <CustomButton
              type="button"
              onClick={handleSubmit(onSubmit)}
              disabled={seatSeactionLoader?.loading}
              startIcon={
                seatSeactionLoader?.loading && (
                  <CircularProgress size={20} color="inherit" />
                )
              }
              sx={{
                paddingInline: "1rem",
                height: "3.375rem",
                borderRadius: "1.688rem",
                border: `1px solid ${theme.colors.border1}`,
                background: "#FFFFFF",
                color: theme.colors.gray1,
                fontWeight: 500,
                fontSize: "0.95rem",
                minWidth: "6rem",
              }}
            >
              Assign
            </CustomButton>
          </Box>

          <ErrorMessage
            name="email"
            errors={errors}
            as="p"
            style={{
              color: "red",
              fontSize: "0.8rem",
              fontWeight: "bold",
              marginTop: "0.5rem",
            }}
          />

          <Typography
            sx={{
              fontWeight: 400,
              fontSize: "1rem",
              color: theme.colors.gray1,
              lineHeight: "1.25rem",
              marginTop: "0.75rem",
            }}
          >
            Additional team members pro-rated at $20/mo
          </Typography>
        </Stack>
      </CreateBoardWrapper>
    </StyledDialog>
  );
};

export default AssignSeatModal;
